import * as React from 'react'

import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { Skeleton } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import {
  CommentThreadContent,
  CommentThreadContent_Channel,
} from './CommentThreadContent'
import { CommentThreadContentSkeleton } from './CommentThreadContent'
import type { CommentThreadContentChannel } from './CommentThreadContent'
import { CommentsContextSection } from '../CommentsContextSection'
import { NewItemHighlight } from '../../components/NewItemHighlight'
import { useCommentInteraction } from './hooks/useCommentInteraction'
import { useCommentState } from './hooks/useCommentState'
import { CommentActions, CommentActions_Channel } from './CommentActions'
import { useCommentData } from '../context/CommentContext'
import type { CommentsPostCard_Post } from '../../fragments/CommentsPostCard_Post'
import { GbpMockPostCard, PostMiniatureButton } from '../../PostMiniature'
import type { GbpMockPostCard_Channel } from '../../PostMiniature'
import type { Service } from '../../graphqlEnums'
import { useIsByPostView } from '../../util/routeUtils'

import styles from './CommentCard.module.css'

import type { JSX } from 'react'

export const CommentCard_Channel = graphql(
  /* GraphQL */ `
    fragment CommentCard_Channel on Channel @_unmask {
      service
      ...CommentThreadContent_Channel
      ...CommentActions_Channel
    }
  `,
  [CommentThreadContent_Channel, CommentActions_Channel],
)

/** @see CommentThreadContentChannel for why this is still an intersection. */
type CommentCardChannel = FragmentOf<typeof CommentCard_Channel> &
  CommentThreadContentChannel

export const AnimatedPostMiniatureFrame = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => (
  <m.div
    key="post-miniature"
    initial={{ opacity: 0, height: 0 }}
    animate={{ opacity: 1, height: 'auto' }}
    exit={{ opacity: 0, height: 0 }}
    transition={{
      duration: 0.2,
      ease: [0.15, 0, 0.35, 1],
    }}
    className={styles.postMiniatureWrapper}
  >
    {children}
  </m.div>
)

const AnimatedPostMiniature = ({
  post,
  channel,
  postText,
}: {
  post: FragmentOf<typeof CommentsPostCard_Post>
  channel: FragmentOf<typeof GbpMockPostCard_Channel> & { service: Service }
  postText?: string | null
}): JSX.Element => {
  const isGbp = channel.service === 'googlebusiness'

  return (
    <AnimatedPostMiniatureFrame>
      {isGbp ? (
        <GbpMockPostCard channel={channel} subtitle={postText} />
      ) : (
        <PostMiniatureButton post={post} />
      )}
    </AnimatedPostMiniatureFrame>
  )
}

type CommentCardViewProps = {
  commentId: string
  expanded: boolean
  resolved: boolean
  dimmed: boolean
  ariaLabel: string
  onClick: (e: React.MouseEvent) => void
  onKeyDown: (event: React.KeyboardEvent) => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  highlight?: React.ReactNode
  miniature?: React.ReactNode
  actions: React.ReactNode
  children: React.ReactNode
}

export const CommentCardView = React.forwardRef<
  HTMLLIElement,
  CommentCardViewProps
>(
  (
    {
      commentId,
      expanded,
      resolved,
      dimmed,
      ariaLabel,
      onClick,
      onKeyDown,
      onMouseEnter,
      onMouseLeave,
      highlight,
      miniature,
      actions,
      children,
    },
    ref,
  ): JSX.Element => (
    <li
      id={commentId}
      ref={ref}
      className={styles.card}
      data-comment-wrapper="true"
      data-comment-id={commentId}
      data-expanded={expanded ? 'true' : 'false'}
      data-resolved={resolved ? 'true' : 'false'}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <m.div
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This motion wrapper is a keyboard-operable card control that also contains nested actions.
        role="button"
        tabIndex={0}
        className={styles.wrapper}
        data-testid="comment-wrapper"
        animate={{
          opacity: dimmed ? 0.6 : 1,
          filter: dimmed ? 'grayscale(1)' : 'grayscale(0)',
        }}
        exit={{ opacity: 0 }}
        transition={{
          opacity: { duration: 0.6, ease: [0.15, 0, 0.35, 1] },
          filter: { duration: 0.6, ease: [0.15, 0, 0.35, 1] },
        }}
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-expanded={expanded}
        aria-label={ariaLabel}
      >
        {highlight}
        <AnimatePresence mode="wait">{miniature}</AnimatePresence>
        <div className={styles.threadContent}>{children}</div>
      </m.div>
      {actions}
    </li>
  ),
)

CommentCardView.displayName = 'CommentCardView'

export const getCommentCardAriaLabel = (
  authorName: string | null | undefined,
  text: string | null | undefined,
): string =>
  `Comment by ${authorName || 'Unknown'}: ${text?.slice(0, 100) || 'No text'}${
    text && text.length > 100 ? '...' : ''
  }`

export const CommentCard = React.forwardRef<
  HTMLLIElement,
  {
    channel: CommentCardChannel
    onExpandNextComment?: (
      fromCommentId?: string,
      onlyRepliable?: boolean,
    ) => void
    onExpandPreviousComment?: () => void
    onManualExpansion?: (commentId: string | null) => void
    expanded?: boolean
    onLocalResolutionChange?: (commentId: string, isResolved: boolean) => void
    isLocallyResolved?: boolean
    /** Marks a comment that just arrived via the pill; plays the entrance flash. */
    isNew?: boolean
  }
>((props, ref): JSX.Element => {
  const {
    channel,
    onExpandNextComment,
    onExpandPreviousComment,
    onManualExpansion,
    expanded = false,
    onLocalResolutionChange,
    isLocallyResolved = false,
    isNew = false,
  } = props
  const comment = useCommentData()
  const isByPostView = useIsByPostView()

  // Manage comment state (loading, reply status, hover, resolution)
  const state = useCommentState({
    expanded,
    onLocalResolutionChange,
    isLocallyResolved,
  })

  // Manage comment interaction (click, keyboard, expansion)
  const interaction = useCommentInteraction({
    onExpandNextComment,
    onExpandPreviousComment,
    onManualExpansion,
    onHoverReset: state.handleMouseLeave,
  })

  return (
    <CommentCardView
      ref={ref}
      commentId={comment.id}
      expanded={expanded}
      resolved={state.isResolved}
      dimmed={state.isResolved && !state.isHovered}
      ariaLabel={getCommentCardAriaLabel(comment.author?.name, comment.text)}
      onClick={interaction.handleCommentClick}
      onKeyDown={interaction.handleKeyDown}
      onMouseEnter={state.handleMouseEnter}
      onMouseLeave={state.handleMouseLeave}
      highlight={
        isNew ? <NewItemHighlight kind="comment" itemId={comment.id} /> : null
      }
      miniature={
        expanded &&
        !state.isResolved &&
        !isByPostView && (
          <AnimatedPostMiniature
            post={comment.post}
            channel={comment.post.channel}
            postText={comment.post.text}
          />
        )
      }
      actions={
        <CommentActions
          channel={channel}
          onLoadingChange={state.handleActionsLoadingChange}
          disabled={state.actionsLoading || state.replyStatus !== 'idle'}
          expanded={expanded}
          isHovered={state.isHovered}
          onExpandNextComment={interaction.handleExpandNextFromAction}
          onCollapse={interaction.handleCollapse}
          isLocallyDismissed={state.isLocallyDismissed}
          isLocallyReplied={state.isLocallyReplied}
          isInterimExpanded={state.isInterimExpanded}
          onDismissed={state.handleDismissed}
          onUndismissed={state.handleUndismissed}
        />
      }
    >
      {expanded && !state.isResolved && (
        <CommentsContextSection
          channel={channel}
          onExpandNextComment={onExpandNextComment}
        />
      )}
      <CommentThreadContent
        key={`comment-${comment.id}`}
        channel={channel}
        onExpandNextComment={interaction.handleExpandNextFromAction}
        expanded={expanded}
        onCollapse={interaction.handleCollapse}
        onReplyStatusChange={state.handleReplyStatusChange}
        isInterimExpanded={state.isInterimExpanded}
        isResolvedLocally={state.isResolvedLocally}
      />
    </CommentCardView>
  )
})

CommentCard.displayName = 'CommentCard'

/**
 * Skeleton loader for CommentCard
 * Matches the grid layout structure of the actual wrapper
 * Uses a plain li element to avoid Flex's display properties conflicting with grid layout
 */
export const CommentCardSkeleton = (): JSX.Element => {
  return (
    <li className={styles.card}>
      <div className={styles.wrapper}>
        <CommentThreadContentSkeleton />
      </div>
      <Skeleton width={30} height={30} />
    </li>
  )
}
